import * as React from 'react';
import { IDiscard } from 'common/schema/Discard';
import { TableScroll } from 'client/ui/table/TableScroll';
import { Button, Alert, message } from 'antd';
import { ColumnProps, TableRowSelection, SorterResult } from 'antd/lib/table';
import { IDiscardDossier } from 'common/schema/DiscardDossier';
import { IDiscardDossiersQueryDto } from 'common/dto/DiscardQueryDto';
import { useApiQuery } from 'client/core/network/hooks/useApiQuery';
import { DiscardApi } from '../../DiscardApi';
import { getNetworkErrorMessage } from 'client/core/network/errors/getNetworkErrorMessage';
import { useState } from 'react';
import { useApiMutationState } from 'client/core/network/hooks/useApiMutation';
import { DiscardLogic } from 'common/logic/DiscardLogic';
import { DiscardItemBadge } from '../../detail/item-badge/DiscardItemBadge';
import { ButtonDeleteConfirm } from 'client/ui/button/ButtonDeleteConfirm';
import { tableLoaderSpin } from 'client/ui/loader/tableLoaderSpin';
import {
  createColumnFilter,
  FilterInputType,
  getFilter
} from 'client/ui/table/TableColumnFilter';
import { ColumnSortDirections } from 'client/ui/table/TableColumnSort';

export interface IDiscardDossiersTableProps {
  discard: IDiscard;
  /**
   * Modalità di modifica
   */
  isEditable?: boolean;
  /**
   * Per mantenere le impostazioni in caso di abilitazione del FooterMenu
   */
  hasFooterMenu?: boolean;
}

/**
 * Elenco di fascicoli contenuti nello scarto.
 */
export function DiscardDossiersTable(props: IDiscardDossiersTableProps) {
  const { discard, isEditable } = props;
  const [query, setQuery] = useState<IDiscardDossiersQueryDto>({
    page: 1,
    size: 20
  });

  const isLoadingContent = DiscardLogic.isLoadingContent(discard);

  const { response, loading, error } = useApiQuery(DiscardApi.findDossiers, {
    skip: isLoadingContent,
    data: { id: discard.id, query }
  });

  // Selezione fascicoli
  const [selected, setSelected] = useState<IDiscardDossier[]>([]);
  const rowSelection: TableRowSelection<IDiscardDossier> = {
    selectedRowKeys: selected?.map(d => String(d.id)),
    onChange: (selectedRowKeys, selectedRows) => {
      setSelected(selectedRows);
    }
  };

  // Rimozione fascicoli dallo scarto
  const [remove, { loading: removeLoading }] = useApiMutationState(
    DiscardApi.removeDossiers,
    {
      data: { id: discard.id },
      invalidates: [DiscardApi.findDossiers, DiscardApi.find]
    }
  );
  const handleRemove = async () => {
    await remove({
      data: { input: { dossierIds: selected.map(d => d.dossierId) } }
    });
    message.success(
      'I fascicoli selezionati sono stati eliminati dalla proposta di scarto'
    );
  };

  const columns: ColumnProps<IDiscardDossier>[] = [
    {
      dataIndex: 'areaUid',
      title: 'A.O.O.',
      ellipsis: true,
      width: 120,
      ...createColumnFilter(FilterInputType.Text)
    },
    {
      dataIndex: 'code',
      title: 'Codice',
      ellipsis: true,
      width: 120,
      ...createColumnFilter(FilterInputType.Text)
    },
    {
      dataIndex: 'title',
      title: 'Titolo',
      ellipsis: true,
      ...createColumnFilter(FilterInputType.Text)
    },
    {
      dataIndex: 'state',
      render: (state, item) => <DiscardItemBadge state={state} />,
      title: 'Stato',
      width: 100
    }
  ];

  const dossiers = response?.data.items;

  return (
    <>
      {error && <Alert type="error" message={getNetworkErrorMessage(error)} />}
      <TableScroll
        bottomSpace={props.hasFooterMenu ? 102 : 64}
        rowKey={record => String(record.id)}
        loading={tableLoaderSpin(loading || isLoadingContent)}
        dataSource={dossiers ?? []}
        columns={columns}
        onChange={(pagination, filters, sorter) => {
          const { field, order } = sorter as SorterResult<IDiscardDossier>;

          const areaUid = getFilter<string>(filters, 'areaUid');
          const code = getFilter<string>(filters, 'code');
          const title = getFilter<string>(filters, 'title');

          setQuery({
            ...query,
            areaUid,
            code,
            title,
            orderBy: order ? field?.toString() : 'id',
            orderByDirection: order
              ? ColumnSortDirections[order]
              : ColumnSortDirections.descend,
            page: pagination.current || query.page,
            size: pagination.pageSize || query.size
          });
        }}
        pagination={{
          size: 'small',
          position: 'bottom',
          showSizeChanger: true,
          total: response?.data.meta?.total,
          pageSize: query.size,
          current: query.page,
          pageSizeOptions: ['5', '10', '20']
        }}
        // label={
        //   editable
        //     ? 'Seleziona i Fascicoli che non devono essere Scartati'
        //     : 'Elenco Fascicoli'
        // }
        rowSelection={isEditable ? rowSelection : undefined}
        bottomLeft={
          isEditable && (
            <ButtonDeleteConfirm
              placement="top"
              title="Sei sicuro di voler escludere i Fascicoli selezionati da questo Scarto?"
              okText="Escludi selezionati dallo Scarto"
              onConfirm={handleRemove}
              loading={removeLoading}
            />
          )
        }
      />
    </>
  );
}
